import { blue, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { JOB_TAG_COLOR_MAP, TJobTagColor } from "@/features/job/constants/job-tag-colors";
import { useTags } from "@/api/services/main/tag";
import { Box, Chip, Popover, Stack, SxProps } from "@mui/material";
import dayjs from "dayjs";
import { FC, MouseEvent, useMemo, useState } from "react";

export type TJobTagListProps = {
  tagIds?: string[] | null | undefined;
  createdAt?: string | null | undefined;
  maxVisible?: number;
};

const NEW_TAG_DISPLAY_DAYS = 14;

const BASE_CHIP_SX: SxProps = {
  fontWeight: "700",
  fontSize: "12px",
  borderRadius: rounded.md,
};

const NEW_CHIP_SX: SxProps = {
  ...BASE_CHIP_SX,
  bgcolor: blue[80],
  color: text.white,
};

const MORE_CHIP_SX: SxProps = {
  ...BASE_CHIP_SX,
  bgcolor: "#E6E7E8",
  color: text.secondary,
  cursor: "pointer",
};

const getTagChipSx = (color?: TJobTagColor): SxProps => ({
  ...BASE_CHIP_SX,
  bgcolor: color?.bg,
  color: color?.border,
  border: "1px solid",
  borderColor: color?.border,
});

type TChipItem = { type: "new" } | { type: "tag"; id: string };

export const JobTagList: FC<TJobTagListProps> = ({ tagIds, createdAt, maxVisible }) => {
  const { data: jobTags } = useTags("job");
  // Map tag id → tag master entry for label/color lookup
  // tags.id is bigint (typed number, runtime string); key by String so it matches string tagIds.
  const tagMap = useMemo(() => new Map(jobTags.map((t) => [String(t.id), t])), [jobTags]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const { isNew, ids } = useMemo(() => {
    const list = Array.isArray(tagIds) ? [...tagIds] : [];
    // NEW: auto-applied within 14 days of creation (system tag, not editable)
    const newFlag = createdAt ? dayjs().diff(dayjs(createdAt).format("YYYY-MM-DD"), "d") <= NEW_TAG_DISPLAY_DAYS : false;
    const uniq = Array.from(new Set(list))
      .filter((id) => tagMap.has(id))
      .sort((a, b) => (tagMap.get(a)?.order ?? 0) - (tagMap.get(b)?.order ?? 0));
    return { isNew: newFlag, ids: uniq };
  }, [tagIds, createdAt, tagMap]);

  if (!isNew && ids.length === 0) return null;

  const items: TChipItem[] = [...(isNew ? [{ type: "new" as const }] : []), ...ids.map((id) => ({ type: "tag" as const, id }))];
  const collapse = maxVisible != null && items.length > maxVisible;
  const visible = collapse ? items.slice(0, maxVisible) : items;
  const hiddenTags = collapse ? items.slice(maxVisible) : [];

  const handleOpenMore = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const handleCloseMore = () => setAnchorEl(null);

  const renderItem = (item: TChipItem) => {
    if (item.type === "new") return <Chip key="__new__" label="NEW" sx={NEW_CHIP_SX} />;
    const tagItem = tagMap.get(item.id);
    const color = tagItem ? JOB_TAG_COLOR_MAP[tagItem.color] : undefined;
    return <Chip key={item.id} label={tagItem?.label ?? String(item.id)} sx={getTagChipSx(color)} />;
  };

  return (
    <Stack direction="row" useFlexGap gap={1}>
      {visible.map(renderItem)}
      {hiddenTags.length > 0 && (
        <>
          <Chip label={`+${hiddenTags.length}`} sx={MORE_CHIP_SX} onClick={handleOpenMore} />
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleCloseMore}
            onClick={(e) => e.stopPropagation()}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            <Box sx={{ p: 1.25, maxWidth: 280 }}>
              <Stack direction="row" useFlexGap flexWrap="wrap" gap={1}>
                {hiddenTags.map(renderItem)}
              </Stack>
            </Box>
          </Popover>
        </>
      )}
    </Stack>
  );
};
